if (!customElements.get("video-block")) {
  customElements.define(
    "video-block",
    class VideoBlock extends HTMLElement {
      constructor() {
        super();
        this.breakpoint = 769;
        // lobal settings
        this.isMobileDifferent = this.dataset.videoMobileUseDesktop === "false";
        this.videoPreload = this.dataset.preload === "true";

        // Desktop settings
        this.videoShowControls = this.dataset.showControls === "true";
        this.videoAutoplay = this.dataset.videoAutoplay === "true";
        this.videoMuted = this.dataset.videoMuted === "true";
        this.videoLoop = this.dataset.videoLoop === "true";
        this.videoControlsStyle = this.dataset.videoControlsStyle || "custom";

        // Mobile settings
        this.videoShowControlsMobile =
          this.dataset.showControlsMobile === "true";
        this.videoMutedMobile = this.dataset.videoMutedMobile === "true";
        this.videoAutoplayMobile = this.dataset.videoAutoplayMobile === "true";
        this.videoLoopMobile = this.dataset.videoLoopMobile === "true";
        this.videoControlsStyleMobile =
          this.dataset.videoControlsStyleMobile || "custom";

        // Elements
        this.videoPosterContainer = this.querySelector(
          ".video-poster-container",
        );
        this.mobileControls = this.querySelector(
          ".video-controls[data-mobile]",
        );
        this.desktopControls = this.querySelector(
          ".video-controls[data-desktop]",
        );

        // Templates
        this.desktopTemplate = this.querySelector("template[data-desktop]");
        this.mobileTemplate =
          this.querySelector("template[data-mobile]") || null;
        this.hasInitialized = false;
        this.resizeListenerInitialized = false;
        this.preloadResizeListenerInitialized = false;
      }

      connectedCallback() {
        if (this.hasInitialized) return;
        this.hasInitialized = true;

        if (!this.videoPreload) {
          this.observer = new IntersectionObserver(
            (entries, observer) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.loadCurrentVideo();
                  observer.unobserve(this);
                }
              });
            },
            { threshold: 0.1 },
          );

          this.observer.observe(this);
        } else {
          if (this.isMobileDifferent && this.mobileControls) {
            this.initMobileControls();
          }
          if (this.desktopControls) {
            this.initDesktopControls();
          }
          this.playAutoplayVideos();
        }

        if (this.isMobileDifferent && !this.videoPreload) {
          this.setupResizeListener();
        } else if (this.isMobileDifferent && this.videoPreload) {
          this.setupPreloadResizeListener();
        }
      }

      disconnectedCallback() {
        this.observer?.unobserve(this);
      }

      loadCurrentVideo() {
        if (window.innerWidth <= this.breakpoint && this.isMobileDifferent) {
          this.loadMobileVideo();
          if (this.videoPosterContainer && this.videoAutoplayMobile) {
            this.removePoster();
          }
        } else {
          this.loadDesktopVideo();
          if (this.videoPosterContainer && this.videoAutoplay) {
            this.removePoster();
          }
        }

        this.playAutoplayVideos();
      }

      loadDesktopVideo() {
        if (!this.desktopTemplate || this.querySelector("video.desktop-video"))
          return;

        const clone = this.desktopTemplate.content.cloneNode(true);
        this.appendChild(clone);
        this.initDesktopControls();

        const video = this.querySelector("video.desktop-video");
        if (video && this.videoAutoplay) {
          video.muted = true;
          video.playsInline = true;
          video.play().catch(() => {});
        }
      }

      loadMobileVideo() {
        if (!this.mobileTemplate || this.querySelector("video.mobile-video"))
          return;

        const mobileVideo = this.mobileTemplate.content.cloneNode(true);
        this.appendChild(mobileVideo);
        this.initMobileControls();

        const video = this.querySelector("video.mobile-video");
        if (video && this.videoAutoplayMobile) {
          video.muted = true;
          video.playsInline = true;
          video.play().catch(() => {});
        }
      }

      playAutoplayVideos() {
        this.querySelectorAll("video[autoplay]").forEach((video) => {
          video.muted = true;
          video.playsInline = true;
          video.play().catch(() => {});
        });
      }

       setupResizeListener() {
         if (this.resizeListenerInitialized) return;
         this.resizeListenerInitialized = true;

         window
           .matchMedia(`(max-width: ${this.breakpoint}px)`)
           .addEventListener("change", (e) => {
             // Store the playback state before removing video
             const currentVideo = this.querySelector("video");
             const wasPlaying = currentVideo && !currentVideo.paused;
             const currentTime = currentVideo?.currentTime || 0;

             this.querySelectorAll("video").forEach((video) => video.remove());

             if (e.matches) {
               this.loadMobileVideo();
               if (this.videoPosterContainer && this.videoAutoplayMobile) {
                 this.videoPosterContainer.style = "display: none;";
               } else if (
                 this.videoPosterContainer &&
                 !this.videoAutoplayMobile
               ) {
                 this.videoPosterContainer.style = "display: block;";
               }
             } else {
               this.loadDesktopVideo();
               if (this.videoPosterContainer && this.videoAutoplay) {
                 this.videoPosterContainer.style = "display: none;";
               } else if (this.videoPosterContainer && !this.videoAutoplay) {
                 this.videoPosterContainer.style = "display: block;";
               }
             }

             // Restore playback state if video was playing
             if (wasPlaying) {
               requestAnimationFrame(() => {
                 const newVideo = this.querySelector("video");
                 if (newVideo) {
                   newVideo.currentTime = currentTime;
                   newVideo.play().catch(() => {});
                   const playTrigger = this.querySelector("play-trigger");
                   if (playTrigger) {
                     playTrigger.setAttribute("playing", "true");
                   }
                 }
               });
             }
           });
       }

       setupPreloadResizeListener() {
         if (this.preloadResizeListenerInitialized) return;
         this.preloadResizeListenerInitialized = true;

         window
           .matchMedia(`(max-width: ${this.breakpoint}px)`)
           .addEventListener("change", (e) => {
             // Store the playback state before switching videos
             const currentVideo = this.querySelector("video");
             const wasPlaying = currentVideo && !currentVideo.paused;
             const currentTime = currentVideo?.currentTime || 0;

             // Get the target video element for the new breakpoint
             const targetVideoSelector = e.matches ? "video.mobile-video" : "video.desktop-video";
             const targetVideo = this.querySelector(targetVideoSelector);

             if (wasPlaying && targetVideo) {
               // Resume playback immediately on the new video
               targetVideo.currentTime = currentTime;
               targetVideo.play().catch(() => {});
               
               // Update play trigger state
               const playTrigger = this.querySelector("play-trigger");
               if (playTrigger) {
                 playTrigger.setAttribute("playing", "true");
               }
             } else {
               // Only pause if video wasn't playing
               this.querySelectorAll("video").forEach((video) => video.pause());

               const playTriggerDesktop =
                 this.desktopControls?.querySelector("play-trigger");
               const playTriggerMobile =
                 this.mobileControls?.querySelector("play-trigger");

               if (playTriggerDesktop) {
                 playTriggerDesktop.setAttribute("playing", "false");
               }
               if (playTriggerMobile) {
                 playTriggerMobile.setAttribute("playing", "false");
               }
             }

             if (e.matches) {
               if (this.videoPosterContainer && this.videoAutoplayMobile) {
                 this.videoPosterContainer.style = "display: none;";
               } else if (
                 this.videoPosterContainer &&
                 !this.videoAutoplayMobile
               ) {
                 this.videoPosterContainer.style = "display: block;";
               }
             } else {
               if (this.videoPosterContainer && this.videoAutoplay) {
                 this.videoPosterContainer.style = "display: none;";
               } else if (this.videoPosterContainer && !this.videoAutoplay) {
                 this.videoPosterContainer.style = "display: block;";
               }
             }
           });
       }

      initDesktopControls() {
        if (
          this.desktopControls &&
          this.videoShowControls &&
          this.videoControlsStyle === "custom"
        ) {
          const playTrigger =
            this.desktopControls.querySelector("play-trigger");
          const muteTrigger =
            this.desktopControls.querySelector("mute-trigger");
          const video = this.querySelector("video.desktop-video");

          if (playTrigger && video) {
            playTrigger.addEventListener("click", () => {
              if (video.paused) {
                video.play();
                playTrigger.setAttribute("playing", "true");
                this.removePoster();
              } else {
                video.pause();
                playTrigger.setAttribute("playing", "false");
              }
            });
          }

          if (muteTrigger && video) {
            muteTrigger.addEventListener("click", () => {
              video.muted = !video.muted;
              muteTrigger.setAttribute("muted", video.muted.toString());
            });
          }

          if (!this.videoLoop) {
            const video = this.querySelector("video.desktop-video");
            const playTrigger =
              this.desktopControls.querySelector("play-trigger");
            video.addEventListener("ended", () => {
              playTrigger.setAttribute("playing", "false");
            });
          }
        }
      }

      initMobileControls() {
        if (
          this.mobileControls &&
          this.videoShowControlsMobile &&
          this.videoControlsStyleMobile === "custom"
        ) {
          const playTrigger = this.mobileControls.querySelector("play-trigger");
          const muteTrigger = this.mobileControls.querySelector("mute-trigger");
          const video = this.querySelector("video.mobile-video");

          if (playTrigger && video) {
            playTrigger.addEventListener("click", () => {
              if (video.paused) {
                video.play();
                playTrigger.setAttribute("playing", "true");
                this.removePoster();
              } else {
                video.pause();
                playTrigger.setAttribute("playing", "false");
              }
            });
          }

          if (muteTrigger && video) {
            muteTrigger.addEventListener("click", () => {
              video.muted = !video.muted;
              muteTrigger.setAttribute("muted", video.muted.toString());
            });
          }

          if (!this.videoLoopMobile) {
            const video = this.querySelector("video.mobile-video");
            const playTrigger =
              this.mobileControls.querySelector("play-trigger");
            video.addEventListener("ended", () => {
              playTrigger.setAttribute("playing", "false");
            });
          }
        }
      }

      removePoster() {
        if (this.videoPosterContainer) {
          this.videoPosterContainer.style = "display: none;";
        }
      }
    },
  );
}
